import { Board, BonusType, ScoreBreakdown, Direction } from './types';
import { BOARD_SIZE } from './constants';

export type BoardType = 'crossplay' | 'scrabble';

export const TILE_VALUES: Record<string, number> = {
  A:1, B:3, C:3, D:2, E:1, F:4, G:2, H:4, I:1, J:8, K:5,
  L:1, M:3, N:1, O:1, P:3, Q:10, R:1, S:1, T:1, U:1, V:4,
  W:4, X:8, Y:4, Z:10, ' ':0,
};

// Crossplay board bonus layout (15x15)
// Source: verified against actual NYT Crossplay game board
export const CROSSPLAY_BONUS_MAP: BonusType[][] = (() => {
  const grid: BonusType[][] = Array.from({length:BOARD_SIZE}, () => Array(BOARD_SIZE).fill('normal') as BonusType[]);

  const setBonus = (positions: [number,number][], type: BonusType) => {
    for (const [r,c] of positions) grid[r][c] = type;
  };

  // Source: crossplayhelper.com board editor (verified 2026-03-06)
  // Row 0: 3L,__,__,3W,__,__,__,2L,__,__,__,3W,__,__,3L
  // Row 1: __,2W,__,__,__,__,3L,__,3L,__,__,__,__,2W,__
  // Row 2: __,__,__,__,2L,__,__,__,__,__,2L,__,__,__,__
  // Row 3: 3W,__,__,2L,__,__,__,2W,__,__,__,2L,__,__,3W
  // Row 4: __,__,2L,__,__,3L,__,__,__,3L,__,__,2L,__,__
  // Row 5: __,__,__,__,3L,__,__,2L,__,__,3L,__,__,__,__
  // Row 6: __,3L,__,__,__,__,__,__,__,__,__,__,__,3L,__
  // Row 7: 2L,__,__,2W,__,2L,__,**,__,2L,__,2W,__,__,2L
  // Row 8: __,3L,__,__,__,__,__,__,__,__,__,__,__,3L,__
  // Row 9: __,__,__,__,3L,__,__,2L,__,__,3L,__,__,__,__
  // Row10: __,__,2L,__,__,3L,__,__,__,3L,__,__,2L,__,__
  // Row11: 3W,__,__,2L,__,__,__,2W,__,__,__,2L,__,__,3W
  // Row12: __,__,__,__,2L,__,__,__,__,__,2L,__,__,__,__
  // Row13: __,2W,__,__,__,__,3L,__,3L,__,__,__,__,2W,__
  // Row14: 3L,__,__,3W,__,__,__,2L,__,__,__,3W,__,__,3L

  // Triple Word (8)
  setBonus([
    [0,3],[0,11],
    [3,0],[3,14],
    [11,0],[11,14],
    [14,3],[14,11],
  ], 'TW');

  // Double Word (8)
  setBonus([
    [1,1],[1,13],
    [3,7],
    [7,3],[7,11],
    [11,7],
    [13,1],[13,13],
  ], 'DW');

  // Triple Letter (20)
  setBonus([
    [0,0],[0,14],
    [1,6],[1,8],
    [4,5],[4,9],
    [5,4],[5,10],
    [6,1],[6,13],
    [8,1],[8,13],
    [9,4],[9,10],
    [10,5],[10,9],
    [13,6],[13,8],
    [14,0],[14,14],
  ], 'TL');

  // Double Letter (20)
  setBonus([
    [0,7],
    [2,4],[2,10],
    [3,3],[3,11],
    [4,2],[4,12],
    [5,7],
    [7,0],[7,5],[7,9],[7,14],
    [9,7],
    [10,2],[10,12],
    [11,3],[11,11],
    [12,4],[12,10],
    [14,7],
  ], 'DL');

  grid[7][7] = 'center';
  return grid;
})();

// Standard Scrabble board bonus layout (15x15)
export const SCRABBLE_BONUS_MAP: BonusType[][] = (() => {
  const grid: BonusType[][] = Array.from({length:BOARD_SIZE}, () => Array(BOARD_SIZE).fill('normal') as BonusType[]);

  const setBonus = (positions: [number,number][], type: BonusType) => {
    for (const [r,c] of positions) grid[r][c] = type;
  };

  setBonus([
    [0,0],[0,7],[0,14],[7,0],[7,14],[14,0],[14,7],[14,14],
  ], 'TW');
  setBonus([
    [1,1],[2,2],[3,3],[4,4],[10,10],[11,11],[12,12],[13,13],
    [1,13],[2,12],[3,11],[4,10],[10,4],[11,3],[12,2],[13,1],
  ], 'DW');
  setBonus([
    [1,5],[1,9],[5,1],[5,5],[5,9],[5,13],[9,1],[9,5],[9,9],[9,13],[13,5],[13,9],
  ], 'TL');
  setBonus([
    [0,3],[0,11],[2,6],[2,8],[3,0],[3,7],[3,14],[6,2],[6,6],[6,8],[6,12],
    [7,3],[7,11],[8,2],[8,6],[8,8],[8,12],[11,0],[11,7],[11,14],
    [12,6],[12,8],[14,3],[14,11],
  ], 'DL');
  grid[7][7] = 'center';
  return grid;
})();

export function getBonusType(row: number, col: number, boardType: BoardType): BonusType {
  return boardType === 'scrabble' ? SCRABBLE_BONUS_MAP[row][col] : CROSSPLAY_BONUS_MAP[row][col];
}

export function createEmptyBoard(boardType: BoardType = 'crossplay'): Board {
  return Array.from({length:BOARD_SIZE}, (_,r) =>
    Array.from({length:BOARD_SIZE}, (_,c) => ({
      letter: null, isBlank: false, isUncertain: false,
      bonus: getBonusType(r, c, boardType),
    }))
  );
}

/** Count set bits (popcount) for bitmask operations. */
function popcount(n: number): number {
  let c = 0;
  while (n) { c++; n &= n - 1; }
  return c;
}

/**
 * Score a word placement.
 *
 * @param tilesFromRack - bitmask: bit i set ↔ word[i] came from the rack (not pre-placed on board)
 * @param blankMask     - bitmask: bit i set ↔ word[i] was played from a blank rack tile (scores 0)
 *
 * Board cells with isBlank=true also score 0 for existing tiles.
 */
// Bingo bonus varies by game type:
//   Scrabble   = 50 pts (official)
//   Crossplay  = 35 pts (NYT Crossplay rules)
export const BINGO_BONUS: Record<BoardType, number> = {
  crossplay: 35,
  scrabble:  50,
};

export function scoreWord(
  word: string,
  row: number,
  col: number,
  direction: Direction,
  board: Board,
  tilesFromRack: number,  // bitmask
  blankMask: number = 0,  // bitmask
  boardType: BoardType = 'crossplay',
): ScoreBreakdown {
  let wordMultiplier = 1;
  const baseLetterScores: number[] = [];
  const letterMultipliers: number[] = [];

  for (let i = 0; i < word.length; i++) {
    const r = direction === 'across' ? row : row + i;
    const c = direction === 'across' ? col + i : col;
    const letter = word[i].toUpperCase();
    const isNew = (tilesFromRack >> i & 1) === 1;
    // Blank rack tiles score 0; board cells with isBlank=true also score 0
    const isBoardBlank = !isNew && board[r][c].isBlank;
    const tileValue = ((blankMask >> i & 1) === 1 || isBoardBlank) ? 0 : (TILE_VALUES[letter] ?? 0);

    // Bonus squares only apply to newly placed tiles
    const bonus = isNew ? board[r][c].bonus : 'normal';

    let lm = 1;
    if (bonus === 'DL') lm = 2;
    else if (bonus === 'TL') lm = 3;

    if (bonus === 'DW' || bonus === 'center') wordMultiplier *= 2;
    else if (bonus === 'TW') wordMultiplier *= 3;

    baseLetterScores.push(tileValue);
    letterMultipliers.push(lm);
  }

  const subtotal = baseLetterScores.reduce((sum, v, i) => sum + v * letterMultipliers[i], 0) * wordMultiplier;
  const bingo = popcount(tilesFromRack) === 7;
  const total = subtotal + (bingo ? BINGO_BONUS[boardType] : 0);

  return { baseLetterScores, letterMultipliers, wordMultiplier, subtotal, bingo, total };
}
